import { useI18n } from "@common/hooks/i18n";
import date from "@common/lib/date";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import React from "react";
import { type GridConfigColumn } from "../../types";
import { getTotalSaleExtraColumns, totalSaleChartTypes } from "../lib";
import { type ProviderProps, type TotalSaleBase, type TotalSalesBaseRequest } from "../types";
import CommonStaticProvider from "./CommonStaticProvider";

export type TotalSalesByCountry = TotalSaleBase & {
  countryName: string;
  currencyCode: string;
};

export type TotalSalesByCountryRequest = TotalSalesBaseRequest;
export type Props = Omit<ProviderProps, "id">;
export const SHOP_TOTAL_SALES_BY_COUNTRY = "5458b57b-9ee7-4b8c-9ba7-fc6bf1294487";

const TotalSalesByCountryProvider = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const fields = React.useMemo(
    (): Data.Api.FormField[] => [
      {
        formControlKey: "DateRange",
        formFieldId: "dateRange",
        formFieldKey: "Date",
        label: i18n.dateRange,
        plainTextValue: `{"from": "${date.extractDate(date.addMonth(new Date(), -1))}","to": "${date.extractDate(
          new Date()
        )}"}`
      }
    ],
    [i18n.dateRange]
  );

  const getChartGridData = React.useCallback((id, data) => api.getTotalSalesByCountry(data as any), [api]);

  const getColumns = React.useCallback((): GridConfigColumn<TotalSalesByCountry>[] => {
    return [
      {
        dataType: "string",
        key: "countryName",
        label: i18n.country
      }
    ];
  }, []);

  const extraColumns = React.useMemo(() => getTotalSaleExtraColumns(i18n), [i18n]);

  return (
    <CommonStaticProvider
      badges={["StartDate"]}
      category="Shop"
      fields={fields}
      isLoading={false}
      onLoaded={props.onLoaded}
    >
      {({ getBadges, getFilters, parameters }) =>
        props.children({
          id: SHOP_TOTAL_SALES_BY_COUNTRY,
          chartTypes: totalSaleChartTypes,
          config: {
            aspectRatio: 2.5,
            colors: [],
            legend: "hide",
            xAxis: {
              key: "countryName"
            },
            yAxis: [{ key: "totalSales", color: "{$Blue}", label: i18n.totalSales }],
            totalRecords: 25,
            yNoData: "hide"
          },
          extraColumns,
          fields,
          hasGrid: true,
          isLoading: false,
          name: i18n.totalSalesByCountry,
          parameters,
          type: "Bar",
          getBadges,
          getChartData: getChartGridData,
          getFilters,
          gridConfig: { columns: [], pageSize: 25, key: "currencyCode" },
          gridColumns: getColumns
        })
      }
    </CommonStaticProvider>
  );
};

export default TotalSalesByCountryProvider;
